import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import React from 'react';
import { isActionKey, preventAction } from './a11y';
import { useFocusControl } from './focus';
import { useBox, useStateWithCallback } from './react';
import useId from './useId';

function usePopup(popupType) {
  var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
      id = _ref.id,
      inputPopupId = _ref.popupId,
      inputButtonId = _ref.buttonId,
      onWrapperBlur = _ref.onWrapperBlur,
      onWrapperKeyDown = _ref.onWrapperKeyDown,
      onButtonClick = _ref.onButtonClick,
      onButtonKeyDown = _ref.onButtonKeyDown,
      positionPopup = _ref.positionPopup,
      focusControl = _ref.focusControl,
      _ref$initialExpanded = _ref.initialExpanded,
      initialExpanded = _ref$initialExpanded === void 0 ? false : _ref$initialExpanded,
      _ref$focusOnClickExpa = _ref.focusOnClickExpand,
      focusOnClickExpand = _ref$focusOnClickExpa === void 0 ? popupType === 'dialog' : _ref$focusOnClickExpa;

  var buttonId = useId(inputButtonId || id && "".concat(id, "-button"));
  var popupId = useId(inputPopupId || id && "".concat(id, "-popup"));
  var setFocus = useFocusControl(focusControl, popupId);

  var _useStateWithCallback = useStateWithCallback(initialExpanded),
      _useStateWithCallback2 = _slicedToArray(_useStateWithCallback, 2),
      expanded = _useStateWithCallback2[0],
      setExpanded = _useStateWithCallback2[1];

  var box = useBox({
    expanded: expanded,
    setFocus: setFocus
  }); // For convenience, you can control focus & visibility with a single call.

  var toggle = React.useCallback(function (expand, focusHint, focusOpt) {
    var isExpanded = box.expanded;
    var shouldUpdateState = expand !== isExpanded;

    if (shouldUpdateState) {
      isExpanded = !isExpanded;
    } // A closed element can't focus by index or text search.


    if (!isExpanded && typeof focusHint !== 'object') {
      focusHint = null;
    }

    if (shouldUpdateState) {
      setExpanded(isExpanded, function () {
        if (focusHint !== undefined) {
          box.setFocus(focusHint, focusOpt);
        }
      });
    } else if (focusHint !== undefined) {
      box.setFocus(focusHint, focusOpt);
    }
  }, [box, setExpanded]);
  React.useLayoutEffect(function () {
    if (positionPopup && expanded) {
      var _button = document.getElementById(buttonId);

      var _popup = document.getElementById(popupId);

      if (_popup) {
        positionPopup(_popup, _button);
      }
    }
  }, [positionPopup, expanded, buttonId, popupId]);
  var closeOnBlur = React.useCallback(function (event) {
    if (onWrapperBlur) {
      onWrapperBlur(event, toggle);
    } // Although blur events aren't cancelable, you can use `preventDefault()`
    // in this case to prevent the `usePopup`'s default behavior.


    if (!event.isDefaultPrevented()) {
      var wrapper = event.currentTarget;
      setTimeout(function () {
        if (!wrapper.contains(document.activeElement)) {
          toggle(false);
        }
      }, 50);
    }
  }, [toggle, onWrapperBlur]);
  var handleCloseKey = React.useCallback(function (event) {
    if (event.key === 'Escape') {
      var _button2$getAttribute;

      var _button2 = document.getElementById(buttonId);

      if ((_button2 === null || _button2 === void 0 ? void 0 : (_button2$getAttribute = _button2.getAttribute) === null || _button2$getAttribute === void 0 ? void 0 : _button2$getAttribute.call(_button2, 'aria-expanded')) === 'true') {
        event.stopPropagation();
        toggle(false, _button2);
      }
    }

    if (onWrapperKeyDown) {
      onWrapperKeyDown(event, toggle);
    }
  }, [toggle, buttonId, onWrapperKeyDown]);
  var toggleOnClick = React.useCallback(function (event) {
    if (onButtonClick) {
      onButtonClick(event, toggle);
    }

    if (!event.isDefaultPrevented()) {
      toggle(undefined, focusOnClickExpand ? 0 : undefined);
    }
  }, [toggle, onButtonClick, focusOnClickExpand]);
  var toggleOnKey = React.useCallback(function (event) {
    if (onButtonKeyDown) {
      onButtonKeyDown(event, toggle);
    }

    if (!event.isDefaultPrevented() && isActionKey(event)) {
      event.preventDefault();
      toggle(undefined, 0);
    }
  }, [toggle, onButtonKeyDown]);
  var wrapperProps = {
    id: id,
    role: 'none',
    // This is needed for closeOnBlur to work on Safari.
    tabIndex: -1,
    onBlur: closeOnBlur,
    onKeyDown: handleCloseKey
  };
  var popupProps = {
    id: popupId,
    tabIndex: -1,
    role: popupType,
    'aria-labelledby': buttonId,
    'aria-hidden': !expanded || undefined
  };
  var buttonProps = {
    id: buttonId,
    role: 'button',
    'aria-haspopup': popupType,
    'aria-controls': popupId,
    'aria-expanded': expanded || undefined,
    onClick: toggleOnClick,
    onKeyDown: toggleOnKey,
    onKeyUp: preventAction
  };
  return {
    expanded: expanded,
    toggle: toggle,
    setFocus: setFocus,
    wrapperProps: wrapperProps,
    buttonProps: buttonProps,
    popupProps: popupProps
  };
}

export default usePopup;